import type {SupportedNetwork} from 'src/types';
import type {OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum} from '@stripe-internal/data-v1/schema/types';
import {message, useIntl} from '@sail/intl';

type Chain = OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum;

export type UsdcNetwork = Exclude<Chain, 'bitcoin'>;

export type NetworkTimingRange = {
  min: number;
  max: number;
  unit: 'seconds' | 'minutes';
};

export type NetworkTiming = {
  detectionTime: NetworkTimingRange;
  processingTime: NetworkTimingRange;
};

export const NETWORK_TIMING: Partial<Record<SupportedNetwork, NetworkTiming>> =
  {
    bitcoin: {
      detectionTime: {min: 20, max: 30, unit: 'minutes'},
      processingTime: {min: 20, max: 30, unit: 'minutes'},
    },
    ethereum: {
      detectionTime: {min: 0, max: 30, unit: 'seconds'},
      processingTime: {min: 1, max: 5, unit: 'minutes'},
    },
  };

export const DEFAULT_TIMING: NetworkTiming = {
  detectionTime: {min: 0, max: 10, unit: 'seconds'},
  processingTime: {min: 0, max: 30, unit: 'seconds'},
};

const timingMessages = {
  rangeMinutes: message<{min: number; max: number}>({
    id: 'crypto.payins.v2.timing.range_minutes',
    description: 'Time range in minutes, e.g. "1–5 minutes"',
    defaultMessage: '{min}–{max} {max, plural, one {minute} other {minutes}}',
  }),
  rangeSeconds: message<{min: number; max: number}>({
    id: 'crypto.payins.v2.timing.range_seconds',
    description: 'Time range in seconds, e.g. "5–10 seconds"',
    defaultMessage: '{min}–{max} {max, plural, one {second} other {seconds}}',
  }),
  lessThanMinutes: message<{max: number}>({
    id: 'crypto.payins.v2.timing.less_than_minutes',
    description: 'Less than a number of minutes, e.g. "< 5 minutes"',
    defaultMessage: '<{max} {max, plural, one {minute} other {minutes}}',
  }),
  lessThanSeconds: message<{max: number}>({
    id: 'crypto.payins.v2.timing.less_than_seconds',
    description: 'Less than a number of seconds, e.g. "< 30 seconds"',
    defaultMessage: '<{max} {max, plural, one {second} other {seconds}}',
  }),
};

export const formatTimingRange = (
  timing: NetworkTimingRange,
  intl: ReturnType<typeof useIntl>,
): string => {
  if (timing.min === 0) {
    return intl.formatMessage(
      timing.unit === 'minutes'
        ? timingMessages.lessThanMinutes
        : timingMessages.lessThanSeconds,
      {max: timing.max},
    );
  }
  return intl.formatMessage(
    timing.unit === 'minutes'
      ? timingMessages.rangeMinutes
      : timingMessages.rangeSeconds,
    {min: timing.min, max: timing.max},
  );
};
